import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { checkPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  aadhar?: string;
  address?: string;
  total_purchases: number;
  created_at: string;
  updated_at: string;
}

interface Purchase {
  id: string;
  date: string;
  itemModel: string;
  imei: string;
  category: string;
  saleAmount: number;
  branch: string;
  invoiceId: string;
}

const CustomerManagement: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('Name A-Z');
  const [customersData, setCustomersData] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [summary, setSummary] = useState({
    totalCount: 0,
    newThisMonth: 0,
    repeatCustomers: 0
  });

  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Dropdown states
  const [isSortByDropdownOpen, setIsSortByDropdownOpen] = useState(false);

  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Use refs to access current values without triggering useCallback dependencies
  const searchTermRef = useRef(searchTerm);
  const sortByRef = useRef(sortBy);
  const paginationRef = useRef(pagination);

  // Update refs when values change
  useEffect(() => { searchTermRef.current = searchTerm; }, [searchTerm]);
  useEffect(() => { sortByRef.current = sortBy; }, [sortBy]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);

  // Fetch customers from database with server-side pagination  
  const fetchCustomers = useCallback(async (page = 1) => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Build query parameters using current ref values
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', paginationRef.current.limit.toString());
      
      // Add search term if provided
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      // Add sorting
      if (sortByRef.current && sortByRef.current !== 'Name A-Z') {
        const sortMap: { [key: string]: string } = {
          'Name A-Z': 'name_asc',
          'Name Z-A': 'name_desc', 
          'Recent first': 'created_desc',
          'Oldest first': 'created_asc',
          'Most purchases': 'purchases_desc'
        };
        params.append('sort', sortMap[sortByRef.current] || 'name_asc');
      }

      // Add branch filter - only include customers from specific branch if not "All Branches"
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
      }


      const response = await fetch(getApiUrl(`customers?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setCustomersData(result.customers || []);
          setPagination({
            page: result.pagination?.page || page,
            limit: result.pagination?.limit || paginationRef.current.limit,
            total: result.pagination?.total || 0,
            totalPages: result.pagination?.totalPages || 0
          });
          // Update summary from API response
          if (result.summary) {
            setSummary({
              totalCount: result.summary.totalCount || 0,
              newThisMonth: result.summary.newThisMonth || 0,
              repeatCustomers: result.summary.repeatCustomers || 0
            });
          }
        } else {
          setError('Failed to fetch customers');
          showToast('Failed to fetch customers', 'error');
        }
      } else {
        setError('Failed to fetch customers');
        showToast('Failed to fetch customers', 'error');
      }
    } catch (error) {
      setError('Error fetching customers');
      showToast('Error fetching customers', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, selectedBranchId, isAllBranches]);

  // Load customers when component mounts or when dependencies change (not searchTerm)
  useEffect(() => {
    if (!token) return;

    fetchCustomers(1);
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [token, sortBy, selectedBranchId, isAllBranches, isInitialLoad, fetchCustomers]);

  // Check permissions
  if (!checkPermission(user?.role, 'customers')) {
    return <AccessDenied resource="Customer Management" />;
  }

  // Dropdown options
  const sortByOptions = [
    'Name A-Z',
    'Name Z-A',
    'Recent customers',
    'Most purchases'
  ];

  // Use summary from API for accurate totals across all customers
  const totalCustomers = summary.totalCount;
  const newCustomersThisMonth = summary.newThisMonth;
  const repeatCustomers = summary.repeatCustomers;

  // Use server-side filtered and sorted data directly
  const currentData = customersData;

  // Close dropdown when clicking outside
  const closeDropdowns = () => {
    setIsSortByDropdownOpen(false);
  };

  // Icons
  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const FilterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );



  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Customer Management</h1>
            <p className="text-gray-500 mt-1">
              Manage customer relationships and track purchase history
              {!isAllBranches && selectedBranchId ? (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  📍 Filtering: {selectedBranchName}
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  🌐 Showing: All Branches
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Branch Filter Info */}
        {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Branch Filter Active
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  Showing only customers who have made purchases in <strong>{selectedBranchName}</strong>. 
                  Select "All Branches" to see customers from all locations.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New This Month</p>
                <p className="text-2xl font-bold text-gray-900">{newCustomersThisMonth}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Repeat Customers</p>
                <p className="text-2xl font-bold text-gray-900">{repeatCustomers}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search - Left side */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Name, phone, or aadhar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchCustomers(1)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                style={{ width: '460px' }}
              />
            </div>
            <button
              onClick={() => fetchCustomers(1)}
              className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors h-10 whitespace-nowrap flex items-center justify-center"
            >
              Search
            </button>
          </div>

          {/* Filters - Right side */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Sort By Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setIsSortByDropdownOpen(!isSortByDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
              >
                <span className="text-sm font-medium text-gray-700">
                  {sortBy}
                </span>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${isSortByDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isSortByDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {sortByOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSortBy(option);
                        setIsSortByDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        sortBy === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => {
                setSearchTerm('');
                setSortBy('Name A-Z');
                setIsSortByDropdownOpen(false);
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-lg hover:border-gray-300 bg-white h-10"
            >
              <FilterIcon />
              Clear Filters
            </button>
          </div>
        </div>
      </div>


      {/* Customer Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aadhar</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Purchases</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Since</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <LoadingSpinner />
                    <p className="text-gray-500 mt-2">Loading customers...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No customers found
                  </td>
                </tr>
              ) : (
                currentData.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.phone_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{customer.aadhar || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="max-w-xs truncate" title={customer.address || 'N/A'}>
                        {customer.address || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {customer.total_purchases}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(customer.created_at).toLocaleDateString('en-IN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-4 sm:mb-0">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pagination.limit}
              onChange={(e) => {
                setPagination(prev => ({ ...prev, limit: Number(e.target.value) }));
                fetchCustomers(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">
              entries (Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total})
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchCustomers(Math.max(1, pagination.page - 1))}
              disabled={pagination.page <= 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, index) => {
              const page = pagination.page <= 3 ? index + 1 : pagination.page - 2 + index;
              if (page > pagination.totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => fetchCustomers(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    page === pagination.page
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => fetchCustomers(Math.min(pagination.totalPages, pagination.page + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default CustomerManagement;